import React, { FC } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import Link from 'next/link';
import { CarouselArrowIconPos } from 'svgs';
import clsx from 'clsx';

interface LinkBoxProps {
  title: string;
  href: string;
  active: boolean;
  hasAccordion?: boolean;
  mainTitle?: string;
  subtitle?: string;
  subtitleHref?: string;
  activeSubtitle?: boolean;
  secondSubtitle?: string;
  secondSubtitleHref?: string;
  activeSecondSubtitle?: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    linkBox: {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(4),
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        '& p': {
          color: theme.palette.common.white,
        },
        '& svg': {
          '& path': {
            fill: theme.palette.common.white,
          },
        },
      },
    },
    linkBoxAccordion: {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(1.9),
      cursor: 'pointer',
      boxShadow: 'none',
      marginTop: theme.spacing(2),
      borderBottomLeftRadius: '0 !important',
      borderBottomRightRadius: '0 !important',
      '&::before': {
        opacity: 0,
      },
    },
    activeLinkBoxAccordion: {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(1.9),
      cursor: 'pointer',
      boxShadow: 'none',
      marginTop: theme.spacing(2),
      borderBottomLeftRadius: '0 !important',
      borderBottomRightRadius: '0 !important',
      '&::before': {
        opacity: 0,
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        borderBottomLeftRadius: '0 !important',
        borderBottomRightRadius: '0 !important',
        '& p': {
          color: theme.palette.common.white,
        },
        '& svg': {
          '& path': {
            fill: theme.palette.common.white,
          },
        },
      },
    },
    accordionSummary: {
      minHeight: '48px !important',
      '& .MuiAccordionSummary-content.Mui-expanded': {
        margin: 0,
      },
      '& .MuiAccordionSummary-expandIcon': {
        transform: 'rotate(0deg)',
      },
      '& .MuiAccordionSummary-expandIcon.Mui-expanded': {
        transform: 'rotate(90deg)',
      },
    },
    accordionDetails: {
      padding: theme.spacing(1, 2, 0),
    },
    divider: {
      height: 1,
      maxWidth: 370,
      backgroundColor: '#E5E5E5',
      marginBottom: theme.spacing(2.5),
    },
    activeLink: {
      backgroundColor: theme.palette.primary.main,
      padding: theme.spacing(4),
      cursor: 'pointer',
      '& p': {
        color: theme.palette.common.white,
      },
      '& svg': {
        '& path': {
          fill: theme.palette.common.white,
        },
      },
    },
    title: {
      color: theme.palette.primary.dark,
    },
    titleAccordion: {
      color: theme.palette.primary.dark,
      padding: theme.spacing(2.5, 4.5),
      marginBottom: 8,
      '&:hover': {
        backgroundColor: theme.palette.primary.contrastText,
      },
    },
    activeTitleAccordion: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      padding: theme.spacing(2.5, 4.5),
      marginBottom: 8,
    },
    icon: {
      marginTop: theme.spacing(0.5),

      '& path': {
        fill: theme.palette.primary.dark,
      },
    },
  });
});

const LinkBox: FC<LinkBoxProps> = (props) => {
  const {
    title,
    href,
    active,
    hasAccordion = false,
    mainTitle,
    subtitle,
    subtitleHref,
    activeSubtitle,
    secondSubtitleHref,
    secondSubtitle,
    activeSecondSubtitle,
  } = props;
  const classes = useStyles();

  return (
    <>
      {hasAccordion ? (
        <Accordion
          expanded={(active || activeSubtitle || activeSecondSubtitle) && true}
          className={
            (active || activeSubtitle || activeSecondSubtitle) && true ? classes.linkBoxAccordion : classes.activeLinkBoxAccordion
          }
        >
          <Link href={href}>
            <AccordionSummary
              expandIcon={<CarouselArrowIconPos position="left" className={classes.icon} />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              className={classes.accordionSummary}
            >
              <Typography variant="body1" className={classes.title}>
                {mainTitle}
              </Typography>
            </AccordionSummary>
          </Link>
          <AccordionDetails className={classes.accordionDetails}>
            <Box display="flex" flexDirection="column" width="100%">
              <Divider className={classes.divider} />
              <Link href={href}>
                <Typography variant="body1" className={clsx(active ? classes.activeTitleAccordion : classes.titleAccordion)}>
                  {title}
                </Typography>
              </Link>
              <Link href={subtitleHref}>
                <Typography
                  variant="body1"
                  className={clsx(activeSubtitle ? classes.activeTitleAccordion : classes.titleAccordion)}
                >
                  {subtitle}
                </Typography>
              </Link>
              {!!secondSubtitle && (
                <Link href={secondSubtitleHref}>
                  <Typography
                    variant="body1"
                    className={clsx(activeSecondSubtitle ? classes.activeTitleAccordion : classes.titleAccordion)}
                  >
                    {secondSubtitle}
                  </Typography>
                </Link>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ) : (
        <Link href={href}>
          <Box
            display="flex"
            justifyContent="space-between"
            mt={2}
            className={clsx(active ? classes.activeLink : classes.linkBox)}
          >
            <Typography variant="body1" className={classes.title}>
              {title}
            </Typography>
            <CarouselArrowIconPos position="left" className={classes.icon} />
          </Box>
        </Link>
      )}
    </>
  );
};

export default LinkBox;
